/*
 * Yotpo reviews on product pages (sections/ra-reviews-yotpo.liquid).
 *
 * - Loads Yotpo's widget loader once, and only if Shopify's consent allows analytics: on the
 *   shopper's first touch, scroll or key press, or once the page has loaded and the browser is
 *   idle, whichever is first. Yotpo takes up to 4 seconds to draw about 2,000px of reviews, so
 *   starting when the section is nearly on screen threw the page about under the shopper.
 * - Every review shows Yotpo's "Verified Buyer" line and the tick on the picture (Curtis,
 *   17 Oct 2026: every reviewer bought from Rod Armour). Yotpo draws them only on the reviews it
 *   marks verified_buyer (and writes "Verified Reviewer" on some others), so verify() copies
 *   Yotpo's own two pieces onto the others, the same markup in the same place, and rewrites that
 *   wording, on every list Yotpo draws (first load, pages, "Load more", sort).
 * - Page turns and "Read more" keep the page still (see hold() and toListTop()): Yotpo's own
 *   answer to a page number was to focus its whole widget and scroll it to the top of the screen,
 *   which threw the shopper up past the summary.
 * - The score shows one decimal and the dates are written out, day first as the reference
 *   wrote them: "13 August 2026" (see score() and date()).
 * - data-cap="3": three reviews, then a "See all N reviews" button that shows the rest in place.
 *   A link marked data-ra-reviews-all (the proof box) and a star-row filter lift the cap too.
 * - Refreshes the page's own rating beside the stars ([data-ra-rating], drawn by
 *   snippets/ra-pdp-rating.liquid from the store's review metafields) from Yotpo's own summary
 *   for that product, so "163 Reviews" under the title is the number the widget shows.
 *   Best matched cards are left on the stored count: no request for them.
 */
const WRITE = 'Write a review';
const ITEMS = '.yotpo-reviews-list-wrapper > .yotpo-reviews-item';
const PAGER = '.yotpo-reviews-pagination-item';

/*
 * Yotpo's own "Verified Buyer" line and picture tick, as it draws them on a verified review
 * (widget of 17 Oct 2026). verify() copies them from a verified review on the page when there is
 * one, so a later change to Yotpo's markup is followed, and uses these only when there is none.
 */
const LABEL_HTML =
  '<div data-v-4c2f4803="" data-v-1ac5f7d8="" style="width: fit-content;"><span data-v-4c2f4803="" class="yotpo-reviewer-verified-buyer-text unselectable" style="align-self: flex-start;">Verified Buyer</span></div>';
const TICK_HTML =
  '<div class="yotpo-verified-icon-wrapper"><svg width="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" class="yotpo-verified-icon" aria-hidden="true"><circle cx="7.5" cy="7.5" r="7.5" fill="#2e4f7c"></circle><path d="M4 7.22222L6.72269 10L11.5 5.5" stroke="white" stroke-width="1.5"></path></svg></div>';

/**
 * While a <ra-reviews> holds still (data-holding: a page turn or a "Read more" was just pressed),
 * Yotpo's focus calls inside it do not scroll the page and its scrollIntoView calls are dropped.
 * Everywhere else both behave as normal. <ra-reviews> scrolls to the list itself (toListTop()).
 */
function quietYotpo() {
  const held = (el) => el instanceof Element && Boolean(el.closest('ra-reviews[data-holding]'));
  const focus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (options) {
    return focus.call(this, held(this) ? { ...options, preventScroll: true } : options);
  };
  const into = Element.prototype.scrollIntoView;
  Element.prototype.scrollIntoView = function (...args) {
    if (!held(this)) into.apply(this, args);
  };
}

/**
 * The review metafields Yotpo syncs to the store lag behind Yotpo itself by a few reviews.
 * Each marked rating that comes near the screen is refreshed from Yotpo's summary for its
 * product: one small request per product, shared by every rating of that product on the page
 * (in practice one request: the page's own product).
 * @param {string} key - The store's public Yotpo key
 */
function refreshRatings(key) {
  /** @type {Map<string, Promise<{total_review?: number, average_score?: number} | null>>} */
  const summaries = new Map();
  const summary = (id) => {
    if (!summaries.has(id)) {
      summaries.set(
        id,
        fetch(`https://api-cdn.yotpo.com/v1/widget/${key}/products/${id}/reviews.json?per_page=1`)
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => data?.response?.bottomline ?? null)
          .catch(() => null)
      );
    }
    return summaries.get(id);
  };

  /** @param {HTMLElement} rating */
  const refresh = async (rating) => {
    const line = await summary(rating.dataset.productId || '');
    const count = Number(line?.total_review);
    const average = Number(line?.average_score);
    if (!(count > 0) || !(average > 0)) return;
    const score = average >= 5 ? '5.0' : (Math.floor(average * 10) / 10).toFixed(1);
    const total = count.toLocaleString('en-US');
    const text = rating.querySelector('[data-ra-rating-text]');
    if (text) text.textContent = text.getAttribute('data-ra-rating-text') === 'score' ? `${score} (${total})` : `${total} ${count === 1 ? 'Review' : 'Reviews'}`;
    const label = rating.querySelector('[data-ra-rating-label]');
    if (label) label.textContent = `Rated ${score} out of 5 stars from `;
    /** @type {HTMLElement | null} */ (rating.querySelector('.ra-stars'))?.style.setProperty('--ra-stars-fill', `${Math.min(100, average * 20)}%`);
    syncLd(rating.dataset.productId || '', score, count, rating.getAttribute('href') === '#ra-reviews');
  };

  const near = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        near.unobserve(entry.target);
        refresh(/** @type {HTMLElement} */ (entry.target));
      }
    },
    { rootMargin: '300px' }
  );
  for (const rating of document.querySelectorAll('[data-ra-rating][data-product-id]')) near.observe(rating);
}

/**
 * Audit 2026-10-21: the product's JSON-LD rating (snippets/ra-structured-data.liquid, from the same metafields as the
 * stars) follows the stars when they are refreshed from Yotpo, so the markup and the page never differ ("5.0 / 165"
 * in the markup beside "4.9 / 166 Reviews"). Only the page's own product (or its main stars), only a rating that is already
 * there, and only when a figure changes.
 * @param {string} id - The product id the stars belong to
 * @param {string} score - "4.9", as the stars show it
 * @param {number} count
 * @param {boolean} [main] - The page's own stars (href #ra-reviews). A pack's stars show its cover's reviews (Yotpo
 *   Bundle Reviews, 2026-10-21), so they carry the cover's id: the page's one product rating follows them too.
 */
function syncLd(id, score, count, main = false) {
  if (!id) return;
  const node =
    document.querySelector(`script[type="application/ld+json"][data-ra-product-ld="${CSS.escape(id)}"]`) ||
    (main ? document.querySelector('script[type="application/ld+json"][data-ra-product-ld]') : null);
  if (!node) return;
  try {
    const data = JSON.parse(node.textContent || '');
    const r = data && data.aggregateRating;
    if (!r || (r.ratingValue === score && r.reviewCount === count && r.ratingCount === count)) return;
    r.ratingValue = score;
    r.reviewCount = count;
    r.ratingCount = count;
    node.textContent = JSON.stringify(data);
  } catch {
    /* markup that cannot be read is left as Shopify printed it */
  }
}

let ratingsStarted = false;

class RaReviews extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-see-all]');
    this.cap = Number(this.dataset.cap) || 0;
    this.lifted = false;

    const near = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      },
      { rootMargin: '600px 0px' }
    );
    near.observe(this);
    this.near = near;

    this.abort = new AbortController();
    const { signal } = this.abort;
    // The reviews are drawn before the shopper can scroll to them: start on the first sign of
    // use (or when the section comes within 600px, above). There is no idle-time start: Yotpo is
    // about 290 KB, and a visit that never touches the page should not download it (page speed).
    const start = () => this.load();
    for (const type of ['touchstart', 'pointerdown', 'wheel', 'keydown']) {
      document.addEventListener(type, start, { once: true, passive: true, capture: true, signal });
    }
    // Page scrolls only, on window and not in capture (code review 2026-10-23 P2, fixed 2026-10-26): a capture
    // listener on document also caught the 'Complete your kit' strip's own auto-scroll, which loaded Yotpo with no
    // shopper input. Scrolls inside an element never reach window.
    window.addEventListener('scroll', start, { once: true, passive: true, signal });
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest('a[href="#ra-reviews"]') : null;
        if (!link) return;
        this.load();
        if (link.hasAttribute('data-ra-reviews-all')) this.lift();
      },
      { signal }
    );
    this.button?.addEventListener('click', () => this.lift(true), { signal });
    this.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('.yotpo-star-distribution-row-wrapper')) this.lift();
      },
      { signal }
    );
    // Capture runs before Yotpo's own handlers, so the hold is on before Yotpo tries to scroll.
    this.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const pager = target?.closest(PAGER);
        if (pager instanceof HTMLButtonElement && !pager.disabled && !pager.matches('.disabled, .selected')) {
          this.hold(4000);
          // Numbered pages swap the list in place: bring its top into view. "Load more" (phones)
          // adds reviews under the ones already read, so the page stays where it is.
          if (!pager.closest('.yotpo-vertical-pagination')) this.toListTop();
        } else if (target?.closest('.yotpo-read-more')) {
          this.hold(800);
        }
      },
      { capture: true, signal }
    );

    new MutationObserver(() => this.tidy()).observe(this, { childList: true, subtree: true });

    const privacy = window.Shopify?.customerPrivacy;
    const allowed = !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
    if (!ratingsStarted && allowed && this.dataset.storeKey) {
      ratingsStarted = true;
      refreshRatings(this.dataset.storeKey);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.near?.disconnect();
    clearTimeout(this.holdTimer);
  }

  /**
   * Holds the box at its height and the page where it is while Yotpo swaps in the next page (or
   * opens a review), so nothing below jumps. Lets go once the new reviews are in (see tidy()), or
   * after `ms` at the latest.
   * @param {number} ms
   */
  hold(ms) {
    clearTimeout(this.holdTimer);
    this.style.minBlockSize = `${this.getBoundingClientRect().height}px`;
    this.toggleAttribute('data-holding', true);
    this.holdFirst = this.reviewKey();
    this.holdTimer = setTimeout(() => this.release(), ms);
  }

  release() {
    clearTimeout(this.holdTimer);
    this.holdFirst = undefined;
    this.removeAttribute('data-holding');
    this.style.removeProperty('min-block-size');
  }

  /** Which reviews are showing: changes when Yotpo draws another page or more reviews. */
  reviewKey() {
    const reviews = this.querySelectorAll('.yotpo-review');
    return `${reviews.length}:${reviews[0]?.getAttribute('data-id') || ''}`;
  }

  /** Scrolls the top of the list just under the header, only when it is above the screen. */
  toListTop() {
    const list = this.querySelector('.yotpo-reviews-list, .yotpo-reviews-list-wrapper');
    if (!list) return;
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const offset = list.getBoundingClientRect().top - header - 16;
    if (offset >= 0) return;
    const top = window.scrollY + offset;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // The theme sets scroll-behavior: smooth on <html>; this one jump is instant.
      const root = document.documentElement;
      const was = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, top);
      root.style.scrollBehavior = was;
    } else {
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  load() {
    if (this.loaded) return;
    const privacy = window.Shopify?.customerPrivacy;
    if (privacy?.analyticsProcessingAllowed && !privacy.analyticsProcessingAllowed()) {
      this.settle();
      return;
    }
    this.loaded = true;
    this.near?.disconnect();
    if (document.querySelector('script[data-ra-yotpo]')) return;
    const script = document.createElement('script');
    script.src = this.dataset.loader || '';
    script.async = true;
    script.dataset.raYotpo = '';
    script.addEventListener('error', () => this.settle());
    document.head.append(script);
  }

  /** Gives up the screen of room the section holds until the widget is drawn (see the section's CSS). */
  settle() {
    this.toggleAttribute('data-settled', true);
  }

  /** Runs on every change Yotpo makes inside the widget. */
  tidy() {
    if (!this.hasAttribute('data-settled') && this.querySelector('.yotpo-bottom-line, .yotpo-reviews-list-wrapper, .yotpo-new-review-btn')) this.settle();
    // The next page (or more reviews) is in: let go of the held height a moment later, once
    // Yotpo has finished drawing it.
    if (this.holdFirst !== undefined && this.reviewKey() !== this.holdFirst) {
      this.holdFirst = undefined;
      clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(() => this.release(), 300);
    }
    // The button always reads "Write a review" (Yotpo writes it other ways, e.g. "Be the first to write a review!").
    for (const button of this.querySelectorAll('.yotpo-new-review-btn')) {
      const text = button.textContent?.trim() || '';
      if (/write\s+a\s+review/i.test(text) && text !== WRITE) button.textContent = WRITE;
    }
    // Reviewer names arrive with a country flag stuck on the end; the name alone is shown.
    for (const name of this.querySelectorAll('.yotpo-reviewer-name')) {
      const plain = name.textContent?.replace(/(?:\uD83C[\uDDE6-\uDDFF]){2}/g, '').replace(/\s{2,}/g, ' ').trim();
      if (plain && plain !== name.textContent?.trim()) name.textContent = plain;
    }
    // Imported reviews have no title of their own: Yotpo cuts one from the start of the text
    // ("They are such an easy") and then prints the full text under it. Mark that title so the
    // CSS hides it; a real title stays.
    const plain = (text) => (text || '').toLowerCase().replace(/[\s.…]+$/u, '').replace(/\s+/g, ' ').trim();
    for (const title of this.querySelectorAll('.yotpo-review-title')) {
      const body = title.closest('.yotpo-review')?.querySelector('.yotpo-review-content');
      const head = plain(title.textContent);
      const dup = Boolean(body && head && plain(body.textContent).startsWith(head));
      if (title.hasAttribute('data-ra-dup') !== dup) title.toggleAttribute('data-ra-dup', dup);
    }
    this.verify();
    this.score();
    for (const date of this.querySelectorAll('.yotpo-review-date')) this.date(date);
    if (!this.cap || this.lifted || !this.button) return;
    const count = this.querySelectorAll(ITEMS).length;
    const more = count > this.cap || (count === this.cap && this.querySelector('.yotpo-reviews-pagination-container'));
    this.toggleAttribute('data-capped', Boolean(more));
    this.button.hidden = !more;
    if (more) {
      const total = this.total();
      const text = total ? `See all ${total.toLocaleString('en-US')} reviews` : 'See all reviews';
      if (this.button.textContent !== text) this.button.textContent = text;
    }
  }

  /**
   * Gives every review Yotpo's "Verified Buyer" line under the name, the tick on the picture and
   * the verified reviewer box's class, copied from a review Yotpo drew them on (or LABEL_HTML /
   * TICK_HTML), marked data-ra-verified.
   * Where Yotpo later draws its own (a page turn can reuse a review's markup), the copy goes.
   * Yotpo's "Verified Reviewer" wording is rewritten to "Verified Buyer".
   */
  verify() {
    const own = (el) => !el.hasAttribute('data-ra-verified');
    const pick = (selector, up = false) =>
      [...this.querySelectorAll(selector)].map((el) => (up ? el.parentElement : el)).find((el) => el && own(el));
    const template = (native, html) => {
      if (native) return native.cloneNode(true);
      const box = document.createElement('div');
      box.innerHTML = html;
      return box.firstElementChild;
    };
    const nativeLabel = pick('.yotpo-reviewer-verified-buyer-text', true);
    const nativeTick = pick('.yotpo-verified-icon-wrapper');
    for (const review of this.querySelectorAll('.yotpo-review')) {
      const name = review.querySelector('.yotpo-reviewer-name');
      const details = name?.parentElement;
      if (details) {
        // Yotpo writes "Verified Reviewer" in the same place on some of the reviews it does not
        // mark as bought; they read "Verified Buyer" like the rest.
        for (const text of details.querySelectorAll('.yotpo-reviewer-verified-buyer-text')) {
          if (text.textContent?.trim() !== 'Verified Buyer') text.textContent = 'Verified Buyer';
        }
        const labels = [...details.querySelectorAll('.yotpo-reviewer-verified-buyer-text')].map((text) => text.parentElement);
        const copies = labels.filter((label) => label && !own(label));
        if (labels.length > copies.length) copies.forEach((copy) => copy?.remove());
        else if (!copies.length) {
          const label = template(nativeLabel, LABEL_HTML);
          if (label) {
            label.setAttribute('data-ra-verified', '');
            name.after(label);
          }
        }
        // Yotpo spaces the verified box on its own (5px above the name, not centred on the
        // picture): the same class, so the name, label and date sit where they do on the others.
        details.classList.replace('yotpo-reviewer-details', 'yotpo-reviewer-details-verified');
      }
      const image = review.querySelector('.yotpo-verified-image-icon');
      if (image) {
        const ticks = [...image.querySelectorAll('.yotpo-verified-icon-wrapper')];
        const copies = ticks.filter((tick) => !own(tick));
        if (ticks.length > copies.length) copies.forEach((copy) => copy.remove());
        else if (!copies.length) {
          const tick = template(nativeTick, TICK_HTML);
          if (tick) {
            tick.setAttribute('data-ra-verified', '');
            image.append(tick);
          }
        }
      }
    }
  }

  /** Yotpo rounds the average to a whole number ("5"); show one decimal, worked out from the star rows. */
  score() {
    let reviews = 0;
    let stars = 0;
    for (const row of this.querySelectorAll('.yotpo-star-distribution-row-wrapper')) {
      const star = parseInt(row.querySelector('.yotpo-star-distribution-index')?.textContent || '', 10);
      const count = parseInt((row.querySelector('.yotpo-star-distribution-value')?.textContent || '').replace(/\D/g, ''), 10);
      if (star > 0 && count >= 0) {
        reviews += count;
        stars += star * count;
      }
    }
    const score = this.querySelector('.yotpo-bottom-line-score, .yotpo-bottomline-score');
    if (!reviews || !score) return;
    const average = stars / reviews;
    const text = average === 5 ? '5.0' : (Math.floor(average * 10) / 10).toFixed(1);
    if (score.textContent?.trim() !== text) {
      score.textContent = text;
      score.setAttribute('aria-label', `${text} out of 5 stars`);
    }
  }

  /**
   * Yotpo prints dates as day/month/year numbers. Write them out instead.
   * @param {Element} el
   */
  date(el) {
    const raw = el.getAttribute('datetime') || el.textContent?.replace(/^Published date\s*/i, '').trim() || '';
    if (!raw || el.getAttribute('data-ra-raw') === raw || el.getAttribute('data-ra-shown') === raw) return;
    const parts = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    let when = null;
    if (parts) {
      const [a, b] = [Number(parts[1]), Number(parts[2])];
      const year = Number(parts[3]) < 100 ? Number(parts[3]) + 2000 : Number(parts[3]);
      // day/month first; month/day only when that is the one reading that makes a real, past date.
      const readings = a > 12 ? [[b, a]] : b > 12 ? [[a, b]] : [[b, a], [a, b]];
      for (const [month, day] of readings) {
        const date = new Date(year, month - 1, day);
        if (date.getMonth() === month - 1 && date.getTime() <= Date.now() + 864e5) {
          when = date;
          break;
        }
      }
    } else if (!Number.isNaN(Date.parse(raw))) {
      when = new Date(raw);
    }
    if (!when) return;
    const days = Math.floor(Math.max(0, Date.now() - when.getTime()) / 864e5);
    const weeks = Math.floor(days / 7);
    let text;
    if (days < 1) text = 'Today';
    else if (days === 1) text = 'Yesterday';
    else if (days < 7) text = `${days} days ago`;
    else if (days < 30) text = weeks === 1 ? 'A week ago' : `${weeks} weeks ago`;
    else text = when.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    el.setAttribute('data-ra-raw', raw);
    el.setAttribute('data-ra-shown', text);
    el.textContent = text;
  }

  /** Yotpo's own total ("Based on 163 reviews"). */
  total() {
    const line = this.querySelector('.yotpo-bottom-line-basic-text, .yotpo-bottom-line-text');
    const match = line?.textContent?.match(/(\d[\d.,\s]*)\s*reviews?\b/i);
    return match ? parseInt(match[1].replace(/\D/g, ''), 10) : 0;
  }

  lift(moveFocus = false) {
    if (this.lifted) return;
    this.lifted = true;
    this.removeAttribute('data-capped');
    if (this.button) this.button.hidden = true;
    if (!moveFocus) return;
    const next = this.querySelectorAll(ITEMS)[this.cap];
    if (next instanceof HTMLElement) {
      next.tabIndex = -1;
      next.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('ra-reviews')) {
  quietYotpo();
  customElements.define('ra-reviews', RaReviews);
}
